import type { RiskRecognitionHighlight, RiskRecognitionMetadata, RiskRecognitionRiskFactor } from '@cobliteam/api-lib/src/dashboard/riskRecognitions/Types';

export interface RiskFactor {
  title: string;
  description: string;
}

export interface DriverDetailsData {
  driverName: string;
  driverPhone: string;
  whatsAppUrl: string;
  vehicleLabel: string;
  locationAddress: string;
  locationUpdatedAt: string;
}

export interface InvestigationData {
  confidence: number | null;
  factors: RiskFactor[];
  recommendations: string[];
}

const PT_NAME_CONNECTORS = new Set(['de', 'da', 'do', 'das', 'dos', 'e']);

/** API sends `driver_name` UPPERCASE; the UI shows it title-cased. */
export const toDriverTitleCase = (raw: string): string =>
  raw.toLocaleLowerCase('pt-BR').replace(/\p{L}[\p{L}'-]*/gu, (word, offset: number) => {
    if (offset > 0 && PT_NAME_CONNECTORS.has(word)) return word;
    return word.charAt(0).toLocaleUpperCase('pt-BR') + word.slice(1);
  });

const buildVehicleLabel = (metadata: RiskRecognitionMetadata | undefined): string => {
  const alias = metadata?.vehicle_alias?.trim() ?? '';
  const plate = metadata?.vehicle_plate?.trim() ?? '';
  if (alias && plate) return `${alias} - ${plate}`;
  return alias || plate;
};

// Mirrors the builder in `modules/drivers` (not imported — module→module rule).
// `message` prefills the chat through wa.me's Click-to-Chat `?text=` param.
export const buildDriverWhatsAppUrl = (rawPhone: string, message?: string): string => {
  const digits = rawPhone.replace(/\D/g, '');
  if (!digits) return '';
  const phone = digits.startsWith('55') ? digits : `55${digits}`;
  const url = `https://wa.me/${phone}`;
  const text = message?.trim();
  return text ? `${url}?text=${encodeURIComponent(text)}` : url;
};

export const toDriverDetails = (highlight: RiskRecognitionHighlight): DriverDetailsData => ({
  driverName: highlight.metadata?.driver_name ? toDriverTitleCase(highlight.metadata.driver_name) : '',
  driverPhone: highlight.metadata?.driver_phone?.trim() ?? '',
  // Prefill the WhatsApp chat with the first (primary) recommendation only.
  whatsAppUrl: buildDriverWhatsAppUrl(highlight.metadata?.driver_phone ?? '', highlight.suggestions?.[0]),
  vehicleLabel: buildVehicleLabel(highlight.metadata),
  locationAddress: highlight.metadata?.last_location?.address ?? '',
  locationUpdatedAt: highlight.metadata?.last_location?.located_at ?? '',
});

const orderFactorsMainFirst = (factors: RiskRecognitionRiskFactor[]): RiskRecognitionRiskFactor[] =>
  [...factors].sort((a, b) => Number(b.type === 'main') - Number(a.type === 'main'));

const FATIGUE_FULL_CONFIDENCE_EVENT_THRESHOLD = 3;

// Fatigue alerts firing on 3+ closed-eye episodes are treated as fully confident:
// the repetition itself is the signal, so we assume 100% and hide the percentage.
const resolveConfidence = (highlight: RiskRecognitionHighlight): number | null => {
  if (highlight.metadata?.rule_name === 'fatigue' && highlight.detection_count >= FATIGUE_FULL_CONFIDENCE_EVENT_THRESHOLD) {
    return 100;
  }
  return highlight.confidence;
};

export const toInvestigation = (highlight: RiskRecognitionHighlight): InvestigationData => ({
  confidence: resolveConfidence(highlight),
  factors: orderFactorsMainFirst(highlight.risk_factors).map(({ title, description }) => ({ title, description })),
  recommendations: highlight.suggestions,
});
